/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { logger, selectHorses, selectPlayers, useTranslation } from '~/app';
import { cn } from '~/utils/styles';

interface ActionEventTesterProps {
  className?: string;
  onClose: () => void;
}

// Custom hook to directly trigger ActionUI events
const useActionUITrigger = () => {
  const { t } = useTranslation();
  const horses = useSelector(selectHorses);
  const players = useSelector(selectPlayers);

  const triggerHorseDropped = (horseIndex: number) => {
    if (players && horses && horses[horseIndex] && players[horses[horseIndex].playerId]) {
      const playerName = players[horses[horseIndex].playerId].name;
      const text =
        t('game_messageHorseDropped', { placeholders: { playerName } }) ||
        `${playerName} hat ein Pferd verloren`;
      logger.debug({ type: 'horseDropped', text }, 'Dispatching horse dropped event');
      // Dispatch a custom event that ActionUI can listen to
      window.dispatchEvent(
        new CustomEvent('action-ui-trigger', {
          detail: {
            type: 'horseDropped',
            text,
          },
        }),
      );
    }
  };

  const triggerNewRound = (round: number) => {
    const text = `Runde ${round + 1} gestartet`;
    logger.debug({ type: 'newRound', text }, 'Dispatching new round event');
    window.dispatchEvent(
      new CustomEvent('action-ui-trigger', {
        detail: {
          type: 'newRound',
          text,
        },
      }),
    );
  };

  const triggerHandicapFailure = (horseIndex: number) => {
    if (players && horses && horses[horseIndex] && players[horses[horseIndex].playerId]) {
      const playerName = players[horses[horseIndex].playerId].name;
      const text =
        t('game_messageHandicap', { placeholders: { playerName } }) ||
        `${playerName} hat eine zu hohe Karte gespielt`;
      logger.debug({ type: 'handicapFailure', text }, 'Dispatching handicap failure event');
      window.dispatchEvent(
        new CustomEvent('action-ui-trigger', {
          detail: {
            type: 'handicapFailure',
            text,
          },
        }),
      );
    }
  };

  const triggerBonusCards = (horseIndex: number) => {
    const horse = horses.find((h) => h.index === horseIndex);
    const playerName = horse ? players[horse.playerId].name : '';
    const text =
      t('game_messageBonuscards', { placeholders: { playerName } }) ||
      `${playerName} hat Bonuskarten verwendet`;
    logger.debug({ type: 'bonusCards', text }, 'Dispatching bonus cards event');
    window.dispatchEvent(
      new CustomEvent('action-ui-trigger', {
        detail: {
          type: 'bonusCards',
          text,
        },
      }),
    );
  };

  const triggerPlayerJoined = (playerName: string) => {
    const text =
      t('game_messagePlayerJoined', { placeholders: { playerName } }) ||
      `${playerName} ist dem Spiel beigetreten`;
    logger.debug({ type: 'playerJoined', text }, 'Dispatching player joined event');
    window.dispatchEvent(
      new CustomEvent('action-ui-trigger', {
        detail: {
          type: 'playerJoined',
          text,
        },
      }),
    );
  };

  const triggerPlayerLeft = (playerName: string) => {
    const text =
      t('game_messagePlayerLeft', { placeholders: { playerName } }) ||
      `${playerName} hat das Spiel verlassen`;
    logger.debug({ type: 'playerLeft', text }, 'Dispatching player left event');
    window.dispatchEvent(
      new CustomEvent('action-ui-trigger', {
        detail: {
          type: 'playerLeft',
          text,
        },
      }),
    );
  };

  return {
    triggerHorseDropped,
    triggerNewRound,
    triggerHandicapFailure,
    triggerBonusCards,
    triggerPlayerJoined,
    triggerPlayerLeft,
  };
};

const ActionEventTester = ({ className, onClose }: ActionEventTesterProps): JSX.Element => {
  const horses = useSelector(selectHorses);
  const [selectedHorseIndex, setSelectedHorseIndex] = useState(0);
  const [currentRound, setCurrentRound] = useState(1);
  const [playerName, setPlayerName] = useState('TestPlayer');
  const {
    triggerHorseDropped,
    triggerNewRound,
    triggerHandicapFailure,
    triggerBonusCards,
    triggerPlayerJoined,
    triggerPlayerLeft,
  } = useActionUITrigger();

  const handleHorseDropped = () => {
    if (horses.length > 0) {
      triggerHorseDropped(selectedHorseIndex);
      onClose();
    }
  };

  const handleNewRound = () => {
    triggerNewRound(currentRound);
    setCurrentRound(currentRound + 1);
    onClose();
  };

  const handleHandicapFailure = () => {
    if (horses.length > 0) {
      triggerHandicapFailure(selectedHorseIndex);
      onClose();
    }
  };

  const handleBonusCards = () => {
    if (horses.length > 0) {
      triggerBonusCards(selectedHorseIndex);
      onClose();
    }
  };

  const handlePlayerJoined = () => {
    triggerPlayerJoined(playerName);
    onClose();
  };

  const handlePlayerLeft = () => {
    triggerPlayerLeft(playerName);
    onClose();
  };

  return (
    <div className={cn('flex flex-col space-y-4', className)}>
      <div className="flex flex-col space-y-2">
        <label htmlFor="horse-select" className="text-sm font-medium">
          Select Horse Index:
        </label>
        <select
          id="horse-select"
          value={selectedHorseIndex}
          onChange={(e) => setSelectedHorseIndex(Number(e.target.value))}
          className="rounded border border-gray-300 px-3 py-2"
        >
          {horses.map((_, index) => (
            <option key={index} value={index}>
              Horse {index}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col space-y-2">
        <label htmlFor="player-name" className="text-sm font-medium">
          Player Name:
        </label>
        <input
          id="player-name"
          type="text"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2"
          placeholder="Enter player name"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 @md:grid-cols-2">
        <button
          onClick={handleHorseDropped}
          className="rounded bg-red-500 px-4 py-2 font-medium text-white hover:bg-red-600"
        >
          Trigger Horse Dropped
        </button>

        <button
          onClick={handleNewRound}
          className="rounded bg-blue-500 px-4 py-2 font-medium text-white hover:bg-blue-600"
        >
          Trigger New Round ({currentRound})
        </button>

        <button
          onClick={handleHandicapFailure}
          className="rounded bg-yellow-500 px-4 py-2 font-medium text-white hover:bg-yellow-600"
        >
          Trigger Handicap Failure
        </button>

        <button
          onClick={handleBonusCards}
          className="rounded bg-green-500 px-4 py-2 font-medium text-white hover:bg-green-600"
        >
          Trigger Bonus Cards
        </button>

        <button
          onClick={handlePlayerJoined}
          className="rounded bg-purple-500 px-4 py-2 font-medium text-white hover:bg-purple-600"
        >
          Trigger Player Joined
        </button>

        <button
          onClick={handlePlayerLeft}
          className="rounded bg-red-500 px-4 py-2 font-medium text-white hover:bg-red-600"
        >
          Trigger Player Left
        </button>
      </div>
    </div>
  );
};

export default ActionEventTester;
